import React, { useState, useEffect, useCallback } from 'react';
import { AIAgent } from '../../types';

interface AgentSelectorProps {
  agents: AIAgent[];
  selectedAgentId: number | null;
  onSelectAgent: (agentId: number) => void;
}

// AIエージェントのデフォルトのアバター画像URL
const DEFAULT_AVATAR_URL = 'https://via.placeholder.com/40?text=AI';

// AgentSelector.scss から内製化（Wave 3d / T20-batch3a）。
// `.agent-item.selected` は SCSS 上で `:hover` より後に定義されており同一詳細度のため
// hover 中も selected の背景が勝つ。したがって selected 側に hover 上書きは付けない。
const AGENT_ITEM =
  'flex items-center gap-3 px-4 py-3 border-b border-b-warm-surface last:border-b-0 cursor-pointer transition-[background-color] duration-200 ease-[ease] hover:bg-warm-hover';
const AGENT_ITEM_SELECTED =
  'flex items-center gap-3 px-4 py-3 border-b border-b-warm-surface last:border-b-0 cursor-pointer transition-[background-color] duration-200 ease-[ease] bg-primary/[0.08]';

const AgentSelector: React.FC<AgentSelectorProps> = ({
  agents,
  selectedAgentId,
  onSelectAgent
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<AIAgent | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredAgents, setFilteredAgents] = useState<AIAgent[]>(agents);

  // 選択中のエージェントを更新
  useEffect(() => {
    if (selectedAgentId && agents.length > 0) {
      const agent = agents.find(a => a.id === selectedAgentId);
      setSelectedAgent(agent || null);
    } else {
      setSelectedAgent(null);
    }
  }, [selectedAgentId, agents]);

  // 検索語句に基づいてエージェントをフィルタリング
  const filterAgents = useCallback((term: string) => {
    if (!term.trim()) {
      return agents;
    }
    
    const normalizedTerm = term.toLowerCase();
    return agents.filter(agent => {
      return (
        agent.title.toLowerCase().includes(normalizedTerm) || 
        (agent.description && agent.description.toLowerCase().includes(normalizedTerm))
      );
    });
  }, [agents]);

  // 検索語句が変更されたときにフィルタリングを実行
  useEffect(() => {
    setFilteredAgents(filterAgents(searchTerm));
  }, [searchTerm, filterAgents]);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      // ドロップダウンを開くときに検索をリセット
      setSearchTerm('');
      setFilteredAgents(agents);
    }
  };

  const handleSelectAgent = (agent: AIAgent) => {
    onSelectAgent(agent.id);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  // 検索アイコンとクローズアイコンのレンダリング関数
  const renderSearchIcon = () => {
    return (
      <span className="absolute left-3 text-gray-600 text-[18px]">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor" width="1em" height="1em">
          <path d="M456.69 421.39L362.6 327.3a173.81 173.81 0 0034.84-104.58C397.44 126.38 319.06 48 222.72 48S48 126.38 48 222.72s78.38 174.72 174.72 174.72A173.81 173.81 0 00327.3 362.6l94.09 94.09a25 25 0 0035.3-35.3zM97.92 222.72a124.8 124.8 0 11124.8 124.8 124.95 124.95 0 01-124.8-124.8z" />
        </svg>
      </span>
    );
  };

  const renderCloseIcon = () => {
    return (
      <span>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor" width="1em" height="1em">
          <path d="M289.94 256l95-95A24 24 0 00351 127l-95 95-95-95a24 24 0 00-34 34l95 95-95 95a24 24 0 1034 34l95-95 95 95a24 24 0 0034-34z" />
        </svg>
      </span>
    );
  };

  // グラデーションアイコンのスタイル生成
  const getIconStyle = (agent: AIAgent) => {
    const baseColor = agent.base_color || '#FFC107';
    return { 
      background: `linear-gradient(135deg, ${baseColor}, ${baseColor}40)`,
      width: '2.5rem',
      height: '2.5rem',
      borderRadius: '50%',
      display: 'inline-block'
    };
  };

  return (
    <div className="relative w-full">
      <div
        className="flex items-center justify-between px-4 py-[10px] bg-warm-card border border-warm-border rounded-lg cursor-pointer transition-all duration-200 ease-[ease] hover:border-primary"
        onClick={toggleDropdown}
      >
        {selectedAgent ? (
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center bg-[#EEF0FA] overflow-hidden [&>img]:w-full [&>img]:h-full [&>img]:object-cover"
              style={getIconStyle(selectedAgent)}
            ></div>
            <span className="font-medium text-dark">{selectedAgent.title}</span>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full flex items-center justify-center bg-[#EEF0FA] overflow-hidden [&>img]:w-full [&>img]:h-full [&>img]:object-cover">🤖</div>
            <span className="font-medium text-dark">AIエージェントを選択</span>
          </div>
        )}
        {/* 開閉状態で排他的な完全文字列を使う（Tailwind の purge は文字列連結を追えないため） */}
        <span className={isOpen
          ? 'text-gray-600 text-[12px] transition-transform duration-200 ease-[ease] rotate-180'
          : 'text-gray-600 text-[12px] transition-transform duration-200 ease-[ease]'}>▼</span>
      </div>

      {isOpen && (
        <div className="absolute top-[calc(100%+8px)] left-0 w-full bg-white border border-warm-border rounded-lg shadow-[0_4px_12px_rgba(0,0,0,0.1)] max-h-[350px] overflow-y-auto z-[100]">
          <div className="px-4 py-3 border-b border-b-warm-border">
            <div className="relative flex items-center">
              {renderSearchIcon()}
              <input
                type="text"
                className="w-full py-[10px] pr-4 pl-10 border border-warm-border rounded-md text-[14px] transition-[border-color] duration-200 ease-[ease] focus:outline-none focus:border-primary placeholder:text-gray-400"
                placeholder="エージェントを検索..."
                value={searchTerm}
                onChange={handleSearchChange}
                onClick={(e) => e.stopPropagation()}
              />
              {searchTerm && (
                <button
                  className="absolute right-3 bg-transparent bg-none border-none text-gray-600 cursor-pointer p-0 flex items-center justify-center hover:text-dark"
                  onClick={(e) => {
                    e.stopPropagation();
                    clearSearch();
                  }}
                >
                  {renderCloseIcon()}
                </button>
              )}
            </div>
          </div>
          
          <div>
            {filteredAgents.length > 0 ? (
              filteredAgents.map(agent => (
                <div
                  key={agent.id}
                  className={selectedAgentId === agent.id ? AGENT_ITEM_SELECTED : AGENT_ITEM}
                  onClick={() => handleSelectAgent(agent)}
                >
                  <div
                    className="w-10 h-10 rounded-full overflow-hidden [&>img]:w-full [&>img]:h-full [&>img]:object-cover"
                    style={getIconStyle(agent)}
                  ></div>
                  <div className="flex-1">
                    <div className="font-medium mb-1 text-dark">{agent.title}</div>
                    <div className="text-[12px] text-gray-600 [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical] overflow-hidden">{agent.description || ''}</div>
                  </div>
                </div>
              ))
            ) : (
              // 旧 `.no-results`: globals.css の @layer components 側の定義
              // （grid-column / bg / rounded）も重なって効いていたため、
              // AgentSelector.scss の値と合成した最終描画をここへ内製化する。
              <div className="col-span-full px-4 py-6 text-center bg-warm-card rounded-lg text-gray-600 text-[14px]">
                「{searchTerm}」に一致するエージェントが見つかりませんでした
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentSelector;
